import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { showToast } from "../primitives";
import { useEntityTypes } from "../../../lib/hooks/use-kuvio";
import {
  useAddWrappedApiMapping,
  useUpdateWrappedApiMapping,
  SYNC_MODES,
  type WrappedApiMapping,
  type WrappedApiOperation,
  type FkRule,
  type SyncMode,
  type MappingInput,
} from "../../hooks/use-wrapped-apis";
import { FieldMapVisual } from "./FieldMapVisual";
import { FkResolutionEditor } from "./FkResolutionEditor";
import { TestMappingDrawer } from "./TestMappingDrawer";

/**
 * Sprint 16c — the response→entity mapping editor (S16C.25). Sections: operation
 * → entity-type header, field mapping, FK resolution, sync mode + TTL, scope
 * filter, and a sample-response test. Opens as a modal over the wrapper detail page.
 */
/** Sprint 16d — AI proposal seed; pre-fills a NEW mapping's target/field-map/sync. */
export interface MappingSeed {
  target_entity_type_slug?: string;
  field_map?: Record<string, string>;
  sync_mode?: SyncMode;
}

export function MappingEditor({
  slug,
  operations,
  existing,
  presetOperationId,
  seed,
  onClose,
}: {
  slug: string;
  operations: WrappedApiOperation[];
  existing?: WrappedApiMapping;
  presetOperationId?: string;
  /** Sprint 16d — AI-proposed defaults for a new mapping (ignored when editing). */
  seed?: MappingSeed;
  onClose: () => void;
}) {
  const entityTypes = useEntityTypes();
  const addMapping = useAddWrappedApiMapping();
  const updateMapping = useUpdateWrappedApiMapping();
  const isEdit = !!existing;

  const [operationId, setOperationId] = useState(existing?.operation_id ?? presetOperationId ?? "");
  const [targetType, setTargetType] = useState(existing?.target_entity_type_slug ?? seed?.target_entity_type_slug ?? "");
  const [fieldMap, setFieldMap] = useState<Record<string, string>>(existing?.field_map ?? seed?.field_map ?? {});
  const [fkRules, setFkRules] = useState<FkRule[]>(existing?.fk_resolution ?? []);
  const [syncMode, setSyncMode] = useState<SyncMode>(existing?.sync_mode ?? seed?.sync_mode ?? "live");
  const [cacheTtl, setCacheTtl] = useState(existing?.cache_ttl_seconds ?? 300);
  const [collectionPath, setCollectionPath] = useState(existing?.collection_path ?? "");
  const [externalIdField, setExternalIdField] = useState(existing?.external_id_field ?? "");
  const [externalTsField, setExternalTsField] = useState(existing?.external_timestamp_field ?? "");
  const [scopeParam, setScopeParam] = useState(existing?.scope_filter?.wrapped_query_param ?? "");
  const [scopePostField, setScopePostField] = useState(existing?.scope_filter?.post_filter_field ?? "");
  const [summaryTemplate, setSummaryTemplate] = useState(existing?.summary_template ?? "");
  const [discoveredPaths, setDiscoveredPaths] = useState<string[]>(Object.keys(existing?.field_map ?? seed?.field_map ?? {}));

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const targetDef = entityTypes.data?.find((t) => t.slug === targetType);
  const targetFields = targetDef?.fields ?? [];
  const pickableTypes = (entityTypes.data ?? []).filter((t) => !t.hide_from_synthesis);

  const saving = addMapping.isPending || updateMapping.isPending;

  async function save() {
    if (!operationId) return showToast("Pick an operation", "error", true);
    if (!targetType) return showToast("Pick a target entity type", "error", true);
    if (Object.keys(fieldMap).length === 0) return showToast("Map at least one field", "error", true);

    const scope_filter =
      scopeParam.trim() || scopePostField.trim()
        ? { ...(scopeParam.trim() ? { wrapped_query_param: scopeParam.trim() } : {}), ...(scopePostField.trim() ? { post_filter_field: scopePostField.trim() } : {}) }
        : null;
    const fk = fkRules.filter((r) => r.wrapped_field && r.target_entity_type && r.matches_entity_field && r.fk_field);

    try {
      if (isEdit) {
        await updateMapping.mutateAsync({
          wrapper_slug: slug,
          operation_id: existing!.operation_id,
          target_entity_type_slug: targetType,
          field_map: fieldMap,
          fk_resolution: fk,
          sync_mode: syncMode,
          cache_ttl_seconds: cacheTtl,
          scope_filter,
          collection_path: collectionPath.trim() || null,
          external_id_field: externalIdField.trim() || null,
          external_timestamp_field: externalTsField.trim() || null,
          summary_template: summaryTemplate.trim() || null,
        });
        showToast("Mapping updated");
      } else {
        const input: MappingInput = {
          wrapper_slug: slug,
          operation_id: operationId,
          target_entity_type_slug: targetType,
          field_map: fieldMap,
          fk_resolution: fk,
          sync_mode: syncMode,
          cache_ttl_seconds: cacheTtl,
          ...(scope_filter ? { scope_filter } : {}),
          ...(collectionPath.trim() ? { collection_path: collectionPath.trim() } : {}),
          ...(externalIdField.trim() ? { external_id_field: externalIdField.trim() } : {}),
          ...(externalTsField.trim() ? { external_timestamp_field: externalTsField.trim() } : {}),
          ...(summaryTemplate.trim() ? { summary_template: summaryTemplate.trim() } : {}),
        };
        await addMapping.mutateAsync(input);
        showToast("Mapping created");
      }
      onClose();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Couldn't save mapping", "error", true);
    }
  }

  return (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label="Edit mapping" style={box}>
        <div style={modalHead}>
          <h2 style={{ fontSize: 16, fontWeight: 500, margin: 0 }}>
            {isEdit ? "Edit mapping" : "New mapping"}
          </h2>
          <button type="button" onClick={onClose} style={closeBtn} aria-label="Close">
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden="true" />
          </button>
        </div>

        <div style={scrollBody}>
          {/* 1 — operation → entity type */}
          <Section title="Operation → entity type">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Operation">
                <select
                  value={operationId}
                  onChange={(e) => setOperationId(e.target.value)}
                  disabled={isEdit}
                  style={input}
                >
                  <option value="">— operation —</option>
                  {operations.map((o) => (
                    <option key={o.operation_key} value={o.operation_key}>
                      {o.method} {o.path}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Target entity type">
                <select value={targetType} onChange={(e) => setTargetType(e.target.value)} style={input}>
                  <option value="">— type —</option>
                  {pickableTypes.map((t) => (
                    <option key={t.slug} value={t.slug}>{t.name}</option>
                  ))}
                </select>
              </Field>
            </div>
            {entityTypes.data && pickableTypes.length === 0 && (
              <div style={hint}>No entity types yet — create one first (create_entity_type) to materialise into.</div>
            )}
            {targetDef && targetDef.activity_tracking === false && (
              <div style={warn}>This type has interaction tracking off, so no Activity will be captured. Enable it on the type to get Activity capture.</div>
            )}
          </Section>

          {/* 2 — field mapping */}
          <Section title="Field mapping">
            {targetType ? (
              <FieldMapVisual
                fields={targetFields}
                fieldMap={fieldMap}
                onChange={setFieldMap}
                discoveredPaths={discoveredPaths}
              />
            ) : (
              <div style={hint}>Pick a target entity type to map fields.</div>
            )}
          </Section>

          {/* 3 — FK resolution */}
          <Section title="FK resolution — link to native entities">
            {targetType ? (
              <FkResolutionEditor
                rules={fkRules}
                onChange={setFkRules}
                targetFields={targetFields}
                entityTypes={pickableTypes}
              />
            ) : (
              <div style={hint}>Pick a target entity type first.</div>
            )}
          </Section>

          {/* 4 — sync mode */}
          <Section title="Sync mode">
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              {SYNC_MODES.map((m) => (
                <Chip key={m} active={syncMode === m} onClick={() => setSyncMode(m)}>{m}</Chip>
              ))}
            </div>
            <div style={hint}>
              {syncMode === "live" && "Every call hits the API. No stale data — safest default."}
              {syncMode === "cache" && "First call materialises + caches; later calls within the TTL serve from the workspace."}
              {syncMode === "hybrid" && "List operations serve from cache; single-get operations hit the API live."}
            </div>
            {syncMode !== "live" && (
              <Field label="Cache TTL (seconds)">
                <input
                  type="number"
                  value={cacheTtl}
                  min={0}
                  onChange={(e) => setCacheTtl(Number(e.target.value))}
                  style={{ ...input, maxWidth: 160 }}
                />
              </Field>
            )}
          </Section>

          {/* 5 — advanced */}
          <Section title="Advanced">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Collection path (array responses)">
                <input value={collectionPath} onChange={(e) => setCollectionPath(e.target.value)} placeholder="tickets" style={{ ...input, fontFamily: "var(--font-mono)" }} />
              </Field>
              <Field label="External ID field (dedupe)">
                <FieldSelect value={externalIdField} onChange={setExternalIdField} fields={targetFields.map((f) => f.name)} />
              </Field>
              <Field label="External timestamp field (conflict)">
                <FieldSelect value={externalTsField} onChange={setExternalTsField} fields={targetFields.map((f) => f.name)} />
              </Field>
              <Field label="Scope query param (server-side)">
                <input value={scopeParam} onChange={(e) => setScopeParam(e.target.value)} placeholder="organization_id" style={{ ...input, fontFamily: "var(--font-mono)" }} />
              </Field>
              <Field label="Scope post-filter field (client-side)">
                <FieldSelect value={scopePostField} onChange={setScopePostField} fields={targetFields.map((f) => f.name)} />
              </Field>
              <Field label="Activity summary override">
                <input value={summaryTemplate} onChange={(e) => setSummaryTemplate(e.target.value)} placeholder="(auto-generated)" style={input} />
              </Field>
            </div>
          </Section>

          {/* 6 — test */}
          <TestMappingDrawer
            fieldMap={fieldMap}
            fkRules={fkRules}
            collectionPath={collectionPath.trim() || null}
            onPathsDiscovered={setDiscoveredPaths}
            onCollectionDetected={(p) => p && setCollectionPath(p)}
          />
        </div>

        <div style={footer}>
          <button type="button" onClick={onClose} style={cancelBtn}>Cancel</button>
          <button type="button" onClick={save} disabled={saving} style={{ ...saveBtn, opacity: saving ? 0.6 : 1 }}>
            {saving ? "Saving…" : isEdit ? "Save mapping" : "Create mapping"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldSelect({ value, onChange, fields }: { value: string; onChange: (v: string) => void; fields: string[] }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={input}>
      <option value="">— none —</option>
      {fields.map((f) => (
        <option key={f} value={f}>{f}</option>
      ))}
    </select>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-primary)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.03em" }}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
      <span style={{ fontSize: 11, fontWeight: 500, color: "var(--color-text-secondary)" }}>{label}</span>
      {children}
    </label>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontSize: 12,
        padding: "5px 14px",
        borderRadius: "var(--border-radius-sm)",
        border: active ? "1px solid var(--color-text-info)" : "var(--kuvio-border-default)",
        background: active ? "var(--c-blue-50, var(--color-background-secondary))" : "var(--color-background-primary)",
        color: active ? "var(--color-text-info)" : "var(--color-text-secondary)",
        cursor: "pointer",
        fontFamily: "var(--font-sans)",
        fontWeight: active ? 500 : 400,
        textTransform: "capitalize",
      }}
    >
      {children}
    </button>
  );
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 200,
  fontFamily: "var(--font-sans)",
  padding: 24,
};
const box: CSSProperties = {
  background: "var(--color-background-primary)",
  border: "0.5px solid var(--color-border-secondary)",
  borderRadius: "var(--border-radius-lg)",
  maxWidth: 760,
  width: "100%",
  maxHeight: "88vh",
  display: "flex",
  flexDirection: "column",
};
const modalHead: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "18px 20px 12px",
  borderBottom: "0.5px solid var(--color-border-secondary)",
};
const scrollBody: CSSProperties = { padding: "16px 20px", overflowY: "auto", flex: 1 };
const footer: CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
  padding: "12px 20px",
  borderTop: "0.5px solid var(--color-border-secondary)",
};
const closeBtn: CSSProperties = { border: "none", background: "none", cursor: "pointer", color: "var(--color-text-tertiary)", padding: 4, display: "inline-flex" };
const input: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "7px 10px",
  fontSize: 12,
  fontFamily: "var(--font-sans)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
};
const hint: CSSProperties = { fontSize: 11, color: "var(--color-text-tertiary)", lineHeight: 1.5, marginTop: 4 };
const warn: CSSProperties = { fontSize: 11, color: "var(--c-amber-800, var(--color-text-secondary))", lineHeight: 1.5, marginTop: 8 };
const cancelBtn: CSSProperties = {
  fontSize: 13,
  padding: "7px 14px",
  borderRadius: "var(--border-radius-sm)",
  border: "none",
  background: "transparent",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const saveBtn: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  padding: "7px 16px",
  borderRadius: "var(--border-radius-sm)",
  border: "none",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
